import { Link, useNavigate } from "react-router-dom";
import "./HomeSection.scss";
import { AlertData } from "../../types/alerts.json";
import { useEffect, useState, useRef } from "react";
import axios from "axios";
import StockSelectionDropdown from "./StockSelectionDropdown";
import { motion, AnimatePresence } from "framer-motion";
import { Search, ArrowRight, Sparkles, MessageCircle, TrendingUp, BarChart2, LineChart, Layers, Zap } from "lucide-react";

const HomeSection = () => {
  const [optionsData, setOptionsData] = useState<any[]>([]);
  const [finalStocks, setFinalStocks] = useState<any[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [searchValue, setSearchValue] = useState("");
  const carouselRef = useRef<HTMLDivElement>(null);
  const autoPlayRef = useRef<NodeJS.Timeout | null>(null);

  const handleSearchSubmit = () => {
    const query = searchValue.trim().toUpperCase();
    if (!query) return;

    // Look for exact match or first starts-with match in optionsData
    const match = optionsData.find(
      (opt) => opt.value.toUpperCase() === query || opt.label.toUpperCase() === query
    ) || optionsData.find(
      (opt) => opt.label.toUpperCase().includes(query)
    );

    if (match) {
      window.localStorage.setItem("selectedTab", "0");
      window.localStorage.setItem("tickerId", match.value);
      window.location.href = `/detail`;
    }
  };

  // Ticker animation state
  const searchTickers = ["WIPRO", "HDFCBANK", "RELIANCE", "INFY", "TCS", "SBIN"];
  const [searchTickerIndex, setSearchTickerIndex] = useState(0);
  const [tickerLoading, setTickerLoading] = useState(false);
  const [showGuideBot, setShowGuideBot] = useState(false);
  const [botMessages, setBotMessages] = useState<{ from: "bot" | "user"; text: string; actions?: { label: string; path: string }[] }[]>([
    { from: "bot", text: "Hi there! I'm your AlgoFabric assistant. How can I help you today?", actions: [
      { label: "What's Trending", path: "/trending" },
      { label: "Search Stocks", path: "/semantic-search" },
      { label: "Dashboard", path: "/dashboard" },
      { label: "Investment Ideas", path: "/investment-ideas" },
      { label: "News Aggregator", path: "/news-aggregator" },
      { label: "Contact Us", path: "/contact" },
    ]},
  ]);
  const [botInput, setBotInput] = useState("");
  const botBodyRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const scrollBotToBottom = () => {
    setTimeout(() => {
      if (botBodyRef.current) botBodyRef.current.scrollTop = botBodyRef.current.scrollHeight;
    }, 50);
  };

  const getBotReply = (msg: string): { text: string; actions?: { label: string; path: string }[] } => {
    const lower = msg.toLowerCase();
    if (lower.includes("trending") || lower.includes("market"))
      return { text: "You can check live market trends, stock alerts, and price movements on What's Trending.", actions: [{ label: "Go to Trending", path: "/trending" }] };
    if (lower.includes("search") || lower.includes("stock") || lower.includes("ticker"))
      return { text: "Use our Semantic Search to find any stock and get AI-powered insights.", actions: [{ label: "Search Stocks", path: "/semantic-search" }] };
    if (lower.includes("dashboard"))
      return { text: "The Dashboard gives you a real-time overview of all tracked stocks with alerts, volume, and sentiment data.", actions: [{ label: "Open Dashboard", path: "/dashboard" }] };
    if (lower.includes("invest") || lower.includes("idea") || lower.includes("strategy"))
      return { text: "Our Investment Ideas page provides AI-driven momentum and value strategies.", actions: [{ label: "View Ideas", path: "/investment-ideas" }] };
    if (lower.includes("news") || lower.includes("article"))
      return { text: "Stay updated with curated financial news from multiple sources on the News Aggregator.", actions: [{ label: "Read News", path: "/news-aggregator" }] };
    if (lower.includes("contact") || lower.includes("support") || lower.includes("help"))
      return { text: "You can reach us at contact@algofabric.ai or visit our Contact page.", actions: [{ label: "Contact Us", path: "/contact" }] };
    if (lower.includes("hi") || lower.includes("hello") || lower.includes("hey"))
      return { text: "Hello! Feel free to ask me about any feature — trending stocks, dashboard, search, or investment ideas." };
    if (lower.includes("what") && lower.includes("algofabric"))
      return { text: "AlgoFabric is an AI-powered financial intelligence platform that combines NLP and data analysis to provide real-time stock insights, alerts, and recommendations." };
    return { text: "I can help you navigate the platform. Try asking about trending stocks, dashboard, search, investment ideas, or news.", actions: [
      { label: "What's Trending", path: "/trending" },
      { label: "Dashboard", path: "/dashboard" },
      { label: "Search Stocks", path: "/semantic-search" },
    ]};
  };

  const handleBotSend = () => {
    const msg = botInput.trim();
    if (!msg) return;
    setBotMessages(prev => [...prev, { from: "user", text: msg }]);
    setBotInput("");
    scrollBotToBottom();
    // Simulate typing delay
    setTimeout(() => {
      const reply = getBotReply(msg);
      setBotMessages(prev => [...prev, { from: "bot", text: reply.text, actions: reply.actions }]);
      scrollBotToBottom();
    }, 600);
  };

  const handleTickerClick = (ticker: string) => {
    setTickerLoading(true);
    window.localStorage.setItem("tickerId", ticker + ".NS");
    window.localStorage.setItem("selectedTab", "0");
    setTimeout(() => {
      navigate("/detail");
    }, 1500);
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setSearchTickerIndex((prev) => (prev + 1) % searchTickers.length);
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  const containerVariants: any = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15, delayChildren: 0.1 }
    }
  };

  const itemVariants: any = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: "easeOut" }
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data: summaryData } = await axios.get(
          `${process.env.REACT_APP_API_URL}/summary.json`
        );

        if (summaryData?.summary) {
          const arr = summaryData.summary.map((item: any) => ({
            value: item.Stock_Ticker,
            label: item.Stock_Ticker,
          }));
          //sort by label
          arr.sort((a: any, b: any) => a.label.localeCompare(b.label));
          setOptionsData(arr);
        }
      } catch (error) {
        console.error(error);
      }
    };

    fetchData();
  }, []);

  const fetchAlertData = async () => {
    try {
      const response = await fetch(
        (process.env.REACT_APP_COMPANY_DOC || "") + "/Alerts/alerts_count.json"
      );
      const data: AlertData = await response.json();
      // console.log(data);
      const filteredStocks: any[] = [];

      Object.entries(data).forEach(([ticker, alerts]: [string, any[]]) => {
        // Count numerical alerts (price change, volume change, ADX, ATR, momentum change, VWAP)
        let count = 0;
        alerts.forEach((alert) => {
          if (
            alert.price_change_alerts ||
            alert.StockVolume_change ||
            alert.ADX_change ||
            alert.ATR_change ||
            alert.VWAP_change ||
            alert.Momentum_change ||
            alert.AD_alert ||
            alert.dpo_alert
          ) {
            count++;
          }
        });

        // Only include stocks with at least 2 numerical alerts
        if (count === 2) {
          filteredStocks.push({
            ticker,
            alerts,
          });
        }
      });
      // console.log("Filtered Stocks: ", filteredStocks);

      // take only 4 stocks to pass in component
      // Get alert ui value for each stock
      const finalStocks = filteredStocks
        .slice(0, 4)
        .map(({ ticker, alerts }) => {
          const alertsModified = alerts.flatMap((alert: any) => {
            // Get the key and value of the alert
            const [alertKey, alertData]: any[] = Object.entries(alert)[0];
            return Object.entries(alertData).flatMap(([key, value]: any) => {
              if (alertKey === "price_change_alerts") {
                return key === "alerts_open_highest"
                  ? {
                      name: "Open Price",
                      value: value.live_day_open,
                      type: "high",
                    }
                  : key === "alerts_open_lowest"
                  ? {
                      name: "Open Price",
                      value: value.live_day_open,
                      type: "low",
                    }
                  : key === "alerts_close_highest"
                  ? {
                      name: "Close Price",
                      value: value.live_day_close,
                      type: "high",
                    }
                  : key === "alerts_close_lowest"
                  ? {
                      name: "Close Price",
                      value: value.live_day_close,
                      type: "low",
                    }
                  : key === "alerts_price_change"
                  ? { name: "Price Change", value: value.percentage_change }
                  : key === "top_n_price_change"
                  ? { name: "Price Change", value: value.percentage_change }
                  : null;
              }

              if (alertKey === "ADX_change") {
                if (key === "weak_trend") {
                  return {
                    name: "ADX Change",
                    value: parseInt(value.ADX).toFixed(2),
                    type: "weak",
                  };
                } else if (key === "strong_trend") {
                  return {
                    name: "ADX Change",
                    value: parseInt(value.ADX).toFixed(2),
                    type: "strong",
                  };
                }
              }

              if (alertKey === "ATR_change") {
                if (key === "low_volatility") {
                  return {
                    name: "ATR Change",
                    value: parseFloat(value.Last_ATR).toFixed(2),
                    type: "low",
                  };
                } else if (key === "high_volatility") {
                  return {
                    name: "ATR Change",
                    value: parseFloat(value.Last_ATR).toFixed(2),
                    type: "high",
                  };
                }
              }

              if (alertKey === "StockVolume_change") {
                if (key === "Stock_volume_high") {
                  return {
                    name: "Stock Volume",
                    value: parseInt(value["Current Volume"]).toFixed(2),
                    type: "high",
                  };
                } else if (key === "Stock_volume_low") {
                  return {
                    name: "Stock Volume",
                    value: parseInt(value["Current Volume"]).toFixed(2),
                    type: "low",
                  };
                }
              }

              if (alertKey === "Momentum_change") {
                if (key === "Oversold") {
                  return {
                    name: "Momentum Change",
                    value: "oversold", // No value provided in the original JSX
                    type: "Oversold",
                  };
                } else if (key === "Overbought") {
                  return {
                    name: "Momentum Change",
                    value: "overbought", // No value provided in the original JSX
                    type: "Overbought",
                  };
                }
              }

              if (alertKey === "VWAP_change") {
                if (key === "Downtrend") {
                  return {
                    name: "VWAP Down",
                    value: parseInt(value.current_vwap).toFixed(2),
                    type: "Down",
                  };
                } else if (key === "Uptrend") {
                  return {
                    name: "VWAP",
                    value: parseInt(value.current_vwap).toFixed(2),
                    type: "Up",
                  };
                }
              }
              if (alertKey === "AD_alert") {
                if (key === "Accumulation") {
                  return {
                    name: "A/D",
                    value: value["Current A/D"].toFixed(2),
                    type: "Up",
                  };
                } else if (key === "Distribution") {
                  return {
                    name: "A/D",
                    value: value["Current A/D"].toFixed(2),
                    type: "Down",
                  };
                }
              }
              if (alertKey === "dpo_alert") {
                if (key === "Upward_cycle") {
                  return {
                    name: "DPO",
                    value: parseInt(value.Current_dpo).toFixed(2),
                    type: "Up",
                  };
                } else if (key === "Downward_cycle") {
                  return {
                    name: "DPO",
                    value: parseInt(value.Current_dpo).toFixed(2),
                    type: "Down",
                  };
                }
              }
            });
          });
          // console.log("Alerts Modified: " + ticker, alertsModified);
          return {
            ticker,
            alerts: alertsModified
              // Remove undefined values
              .filter((ele: any) => ele),
          };
        });
      // console.log("Final Stocks: ", finalStocks);
      setFinalStocks(finalStocks);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchAlertData();
  }, []);

  // Auto-play carousel
  useEffect(() => {
    if (finalStocks.length > 0) {
      autoPlayRef.current = setInterval(() => {
        setCurrentIndex((prevIndex) => {
          return (prevIndex + 1) % finalStocks.length;
        });
      }, 3000); // Change every 3 seconds

      return () => {
        if (autoPlayRef.current) {
          clearInterval(autoPlayRef.current);
        }
      };
    }
  }, [finalStocks.length]);

  const handlePrev = () => {
    if (autoPlayRef.current) {
      clearInterval(autoPlayRef.current);
    }
    setCurrentIndex((prevIndex) => {
      return prevIndex === 0 ? finalStocks.length - 1 : prevIndex - 1;
    });
  };

  const handleNext = () => {
    if (autoPlayRef.current) {
      clearInterval(autoPlayRef.current);
    }
    setCurrentIndex((prevIndex) => {
      return (prevIndex + 1) % finalStocks.length;
    });
  };

  const goToSlide = (index: number) => {
    if (autoPlayRef.current) {
      clearInterval(autoPlayRef.current);
    }
    setCurrentIndex(index);
  };

  // NOTE: The hero must render immediately. It does not use `finalStocks`
  // (that state only feeds a carousel that is no longer rendered), so gating
  // the whole hero on the alerts fetch caused a flash where the next section
  // ("What's Trending?") briefly appeared at the top before the hero mounted.

  return (
    <section className="landing-hero-section-wrapper">
      {/* Ticker loading overlay */}
      {tickerLoading && (
        <div className="ticker-loading-overlay">
          <div className="loadingspinner">
            <div id="square1"></div>
            <div id="square2"></div>
            <div id="square3"></div>
            <div id="square4"></div>
            <div id="square5"></div>
          </div>
          <p>Loading {searchTickers[searchTickerIndex]}.NS...</p>
        </div>
      )}
      {/* Soft floating 2D/3D accents */}
      <motion.div
        className="hero-orb orb-one"
        animate={{ y: [0, -18, 0], x: [0, 10, 0], rotate: [0, 10, 0] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="hero-orb orb-two"
        animate={{ y: [0, 14, 0], x: [0, -12, 0], rotate: [0, -12, 0] }}
        transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
      />

      <div className="landingv2-home-section">
        {/* Hero content */}
        <motion.div
          className="hero-content"
          variants={containerVariants}
          initial="visible"
          animate="visible"
        >
          {/* Badge */}
          <motion.div className="ai-badge" variants={itemVariants}>
            {/* <span className="dot"></span> */}
            AI-Powered Financial Intelligence
            <Sparkles size={16} color="#64748b" />
          </motion.div>

          {/* Main title */}
          <motion.h1 className="home-title" variants={itemVariants}>
            <span className="title-primary">AlgoFabric : A Portfolio/Wealth</span>
            <span className="text-secondary">Management Platform</span>
            <div className="gradient-line"></div>
          </motion.h1>

          <motion.p className="home-subtitle" variants={itemVariants}>
            From conversational AI insights to global multi-broker execution, we empower investors to move from insight to action with unprecedented precision.
          </motion.p>

          {/* Search Ticker bar */}
          <motion.div className="search-wrapper" variants={itemVariants}>
             <div className="search-icon">
                <Search size={22} color="#9ca3af" />
             </div>
             <div className="search-input-container">
               <StockSelectionDropdown
                 stocks={optionsData}
                 searchValue={searchValue}
                 setSearchValue={setSearchValue}
                 onSearchSubmit={handleSearchSubmit}
               />
             </div>
             <button className="search-btn" onClick={handleSearchSubmit}>
               Search <ArrowRight size={18} />
             </button>
          </motion.div>
          <motion.p variants={itemVariants} className="try-searching" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "0.25rem" }}>
            Try searching: 
            <span style={{ position: "relative", display: "inline-flex" }}>
              <AnimatePresence>
                <motion.span
                  key={searchTickers[searchTickerIndex]}
                  initial={{ opacity: 0, filter: "blur(6px)", y: 8 }}
                  animate={{ opacity: 1, filter: "blur(0px)", y: 0 }}
                  exit={{ opacity: 0, filter: "blur(6px)", y: -8 }}
                  transition={{ duration: 0.4 }}
                  style={{ position: "absolute", left: 0, cursor: "pointer", fontWeight: 700 }}
                  onClick={() => handleTickerClick(searchTickers[searchTickerIndex])}
                >
                  {searchTickers[searchTickerIndex]}
                </motion.span>
              </AnimatePresence>
              {/* Invisible spacer to maintain the width for the longest word */}
              <span style={{ visibility: "hidden" }}>HDFCBANK</span>
            </span>
          </motion.p>

          {/* Feature Pills */}
          <motion.div className="hero-feature-pills" variants={itemVariants}>
            <motion.div whileHover={{ y: -3, rotateX: 10 }} transition={{ duration: 0.2 }} className="feature-pill"><TrendingUp size={16} className="pill-icon" /> Real-time Analysis</motion.div>
            <motion.div whileHover={{ y: -3, rotateX: 10 }} transition={{ duration: 0.2 }} className="feature-pill"><BarChart2 size={16} className="pill-icon" /> Market Insights</motion.div>
            <motion.div whileHover={{ y: -3, rotateX: 10 }} transition={{ duration: 0.2 }} className="feature-pill"><LineChart size={16} className="pill-icon" /> AI Predictions</motion.div>
            <motion.div whileHover={{ y: -3, rotateX: 10 }} transition={{ duration: 0.2 }} className="feature-pill"><Layers size={16} className="pill-icon" /> Strategy Deployment</motion.div>
            <motion.div whileHover={{ y: -3, rotateX: 10 }} transition={{ duration: 0.2 }} className="feature-pill"><Zap size={16} className="pill-icon" /> Trade Execution</motion.div>
          </motion.div>

          {/* Hero Stats */}
          <motion.div className="hero-stats" variants={itemVariants}>
            <div className="stat-item">
              <h2>5K+</h2>
              <p>Smart Live Signals</p>
            </div>
            <div className="stat-item">
              <h2>200+</h2>
              <p>Tickers Analyzed</p>
            </div>
            <div className="stat-item">
              <h2>20+</h2>
              <p>Different Alerts</p>
            </div>
          </motion.div>

        </motion.div>
      </div>

      {/* Guide Bot */}
      <div className={`guide-bot-wrapper ${showGuideBot ? "open" : ""}`}>
        {showGuideBot && (
          <div className="guide-bot-panel">
            <div className="gb-header">
              <div className="gb-header-left">
                <div className="gb-avatar">
                  <Sparkles size={16} />
                </div>
                <div>
                  <div className="gb-name">AlgoFabric Assistant</div>
                  {/* <div className="gb-status"><span className="gb-online-dot" /> Online</div> */}
                </div>
              </div>
              <button className="gb-close" onClick={() => setShowGuideBot(false)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="gb-body" ref={botBodyRef}>
              {botMessages.map((msg, i) => (
                <div key={i}>
                  <div className={`gb-msg ${msg.from === "bot" ? "gb-bot" : "gb-user"}`}>
                    <p>{msg.text}</p>
                  </div>
                  {msg.actions && (
                    <div className="gb-actions">
                      {msg.actions.map((action) => (
                        <button
                          key={action.path}
                          className="gb-action-btn"
                          onClick={() => { setShowGuideBot(false); navigate(action.path); }}
                        >
                          {action.label}
                          <ArrowRight size={14} className="gb-arrow" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
            <form className="gb-input-bar" onSubmit={(e) => { e.preventDefault(); handleBotSend(); }}>
              <input
                type="text"
                className="gb-input"
                placeholder="Ask me anything..."
                value={botInput}
                onChange={(e) => setBotInput(e.target.value)}
                autoFocus
              />
              <button type="submit" className="gb-send-btn" disabled={!botInput.trim()}>
                <ArrowRight size={18} />
              </button>
            </form>
          </div>
        )}
        <button className="chat-button-fixed" onClick={() => setShowGuideBot((prev: boolean) => !prev)}>
          {showGuideBot ? (
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2.5" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
          ) : (
            <MessageCircle size={28} color="#ffffff" />
          )}
        </button>
      </div>
    </section>
  );
};

export default HomeSection;
